import {AstView, EnrichedAstNode, LayoutAbstraction, SpacingStrategy} from '../types';
import {recalculateInnerPosition, visitor, isNodeInsideBox, visitorAsync} from '../helpers';
import {calculateAlignProperty, regroupAndReOrderChildren} from './ConvertAbsToFlex/AdjustFlexChildren';
import {findNodesThatNeedAbsolutePositioning} from './ConvertAbsToFlex/FindOverlaps';
import {pickFlexDirection, selectJustifyMode, addAbsChildrenBackIn} from './ConvertAbsToFlex';
import {addNoteToCallStack} from '../helpers/CallStackContextHelper';
import {findPercentageWhiteSpace} from './GapMethods';

export const calculateLayoutSettings = async (tree: EnrichedAstNode, view: AstView) => {
    visitor(tree, view.meta, (currentNode) => {
        removePadding(currentNode);
        removeMargin(currentNode);
    });

    await visitorAsync(tree, view.meta, async (currentNode, meta, parent) => {
        await calculateLayout(currentNode, parent, view);
    });
};

export const checkForRootOverflow = (tree: EnrichedAstNode, view: AstView) => {
    visitor(tree, view.meta, (currentNode) => {
        if (currentNode.styles['overflow'] === 'hidden') {
            let nodeOverflows = false;
            currentNode.children.forEach((child) => {
                const {inside} = isNodeInsideBox(child, currentNode.position);
                if (!inside) {
                    nodeOverflows = true;
                }
            });

            if (!nodeOverflows) {
                delete currentNode.styles['overflow'];
            }
        }
    });
};

export const calculateLayout = async (
    currentNode: EnrichedAstNode,
    parent: EnrichedAstNode | undefined,
    view: AstView
) => {
    // clear padding and recalculate position.

    const inputLayoutAbstraction = currentNode.inputLayoutAbstraction;

    const systemPreference = currentNode.systemLayoutAbstraction;

    currentNode.children.forEach((child) => {
        child.systemLayoutAbstraction.absolutePosition = false;
    });

    const absChildrenIds = findNodesThatNeedAbsolutePositioning(currentNode); // TODO fix bug where this is ran twice and marks everything abs

    const flexChildren = currentNode.children.filter((child) => !absChildrenIds.includes(child.internalId));

    const flexDirection =
        inputLayoutAbstraction.flexDirection || (await pickFlexDirection(currentNode, flexChildren, view));
    const absChildren = currentNode.children.filter((child) => absChildrenIds.includes(child.internalId));

    absChildren.forEach((child) => {
        child.systemLayoutAbstraction.absolutePosition = true;
    });

    const justifyContent =
        inputLayoutAbstraction.justifyContent || selectJustifyMode(currentNode, flexDirection, flexChildren);

    const spacing = spacingStrategy(currentNode, flexChildren, absChildren, parent, inputLayoutAbstraction);

    const calculatedUnits = calculateUnits(inputLayoutAbstraction, spacing);

    const correctFlexChildren = regroupAndReOrderChildren(view, currentNode, flexChildren, flexDirection, 'calculate');

    currentNode.children = addAbsChildrenBackIn(correctFlexChildren, absChildren);

    const settings: LayoutAbstraction = {
        absolutePosition: inputLayoutAbstraction.absolutePosition || systemPreference.absolutePosition || false,
        widthSpacingStrategy: spacing.widthSpacingStrategy,
        heightSpacingStrategy: spacing.heightSpacingStrategy,
        alignSelf: findAlignProperty(currentNode, inputLayoutAbstraction, parent),
        borderBox: 'border-box', // getBoxSizing(parent?.calculatedLayoutAbstraction?.flexDirection, spacing),
        flexDirection: flexDirection,
        justifyContent: justifyContent,
        height: inputLayoutAbstraction.height || undefined,
        width: inputLayoutAbstraction.width || undefined,
        maxWidth: inputLayoutAbstraction.maxWidth || false,
        units: calculatedUnits,
        verticalAnchor: inputLayoutAbstraction.verticalAnchor || 'top',
        horizontalAnchor: inputLayoutAbstraction.horizontalAnchor || 'left',
        userPaddingSet: inputLayoutAbstraction.userPaddingSet,
        userMarginSet: inputLayoutAbstraction.userMarginSet,
        preserveAspectRatio: inputLayoutAbstraction.preserveAspectRatio ?? currentNode.type === 'Image',
    };

    if (
        inputLayoutAbstraction.absolutePosition !== undefined &&
        inputLayoutAbstraction.absolutePosition !== settings.absolutePosition
    ) {
        addNoteToCallStack('Absolute position overriden');
    }
    currentNode.calculatedLayoutAbstraction = settings;

    return {parent: currentNode};
};
export function calculateUnits(
    inputLayoutAbstraction: Partial<LayoutAbstraction>,
    spacing: {
        widthSpacingStrategy: SpacingStrategy;
        heightSpacingStrategy: SpacingStrategy;
    }
) {
    const units = inputLayoutAbstraction.units || {};

    if (spacing.heightSpacingStrategy === 'fill' && !['%', 'vw'].includes(units.height || '')) {
        units.height = '%';
    }
    if (spacing.widthSpacingStrategy === 'fill' && !['%', 'vw'].includes(units.width || '')) {
        units.width = '%';
    }

    if (spacing.heightSpacingStrategy === 'fixed' && !['px', 'rem', 'em'].includes(units.height || '')) {
        units.height = 'px';
    }
    if (spacing.widthSpacingStrategy === 'fixed' && !['px', 'rem', 'em'].includes(units.width || '')) {
        units.width = 'px';
    }

    return units;
}

const FIXED_SIZE_THRESHOLD_PX = 100;

function spacingStrategy(
    node: EnrichedAstNode,
    nodeFlexChildren: EnrichedAstNode[],
    nodeAbsChildren: EnrichedAstNode[],
    parent: EnrichedAstNode | undefined,
    inputLayoutAbstraction: Partial<LayoutAbstraction>
): {
    widthSpacingStrategy: SpacingStrategy;
    heightSpacingStrategy: SpacingStrategy;
} {
    const calculated: {
        widthSpacingStrategy: SpacingStrategy;
        heightSpacingStrategy: SpacingStrategy;
    } = {
        widthSpacingStrategy: 'fill',
        heightSpacingStrategy: 'hug',
    };

    if (node.type === 'Text') {
        if (node.meta.resize === 'WIDTH_AND_HEIGHT') {
            calculated.widthSpacingStrategy = 'hug';
            calculated.heightSpacingStrategy = 'hug';
        } else if (node.meta.resize === 'HEIGHT') {
            calculated.widthSpacingStrategy = 'fill';
            calculated.heightSpacingStrategy = 'hug';
        } else {
            calculated.widthSpacingStrategy = 'fill';
            calculated.heightSpacingStrategy = 'fill';
        }
        return calculated;
    }

    // width only
    if (!parent) {
        calculated.widthSpacingStrategy = 'fill';
        calculated.heightSpacingStrategy = 'hug';
    } else {
        calculated.widthSpacingStrategy = findWidthSpacing(parent, node, nodeFlexChildren, nodeAbsChildren);
    }

    if (node.type === 'Frame' && nodeFlexChildren.length === 0 && calculated.heightSpacingStrategy === 'hug') {
        calculated.heightSpacingStrategy = 'fixed';
    }

    if (node.type === 'Frame' && nodeFlexChildren.length === 0 && calculated.widthSpacingStrategy === 'hug') {
        calculated.widthSpacingStrategy = 'fixed';
    }

    const childPercentages = findPercentageWhiteSpace(node, nodeFlexChildren);
    if (
        node.type === 'Frame' &&
        nodeAbsChildren.length === 0 &&
        calculated.heightSpacingStrategy === 'hug' &&
        childPercentages.yPercentage > 0.3
    ) {
        calculated.heightSpacingStrategy = 'fixed';
    }

    if (
        node.type === 'Frame' &&
        nodeFlexChildren.length === 0 &&
        calculated.widthSpacingStrategy === 'fill' &&
        parent?.calculatedLayoutAbstraction?.widthSpacingStrategy === 'hug'
    ) {
        calculated.widthSpacingStrategy = 'fixed';
    }

    if (node.type === 'Frame' && node.position.width < FIXED_SIZE_THRESHOLD_PX) {
        calculated.widthSpacingStrategy = 'fixed';
    }

    if (
        node.type === 'Frame' &&
        parent &&
        parent.calculatedLayoutAbstraction.widthSpacingStrategy === 'hug' &&
        calculated.widthSpacingStrategy === 'fill'
    ) {
        calculated.widthSpacingStrategy = 'fixed';
    }

    if (node.type === 'Image') {
        if (node.position.width > FIXED_SIZE_THRESHOLD_PX) {
            calculated.widthSpacingStrategy = 'hug';
        } else {
            calculated.widthSpacingStrategy = 'fixed';
        }
    }

    // if is only a small percent of parent should set to hug.

    return {
        widthSpacingStrategy: inputLayoutAbstraction.widthSpacingStrategy || calculated.widthSpacingStrategy,
        heightSpacingStrategy: inputLayoutAbstraction.heightSpacingStrategy || calculated.heightSpacingStrategy,
    };
}

const findAlignProperty = (
    node: EnrichedAstNode,
    inputLayoutAbstraction: Partial<LayoutAbstraction>,

    parent: EnrichedAstNode | undefined
) => {
    if (inputLayoutAbstraction.alignSelf) {
        return inputLayoutAbstraction.alignSelf;
    }
    if (parent) {
        return calculateAlignProperty(parent, parent.calculatedLayoutAbstraction!.flexDirection, node);
    }
    return 'flex-start';
};

/**
 * If the flexChildren on a node occupy more than 80 percent of the parent's outer dimension we will set all to fill.
 * Otherwise children are set to hug.
 */
const CHILD_PERCENT_TO_FILL = 0.4;
const CHILD_PERCENT_TO_STRETCH = 0.65;
/**
 * If width is over child percent to fill we do fill otherwise we do hug/ fixed
 */
function findWidthSpacing(
    parent: EnrichedAstNode | null,
    child: EnrichedAstNode,
    nodeFlexChildren: EnrichedAstNode[],
    nodeAbsChildren: EnrichedAstNode[]
) {
    if (!parent) {
        throw new Error('no parent found');
    }

    const parentSize = parent.innerPosition.width;

    const childrenSize = child.position.width;

    const ratio = childrenSize / parentSize;

    if (ratio < CHILD_PERCENT_TO_FILL) {
        if (nodeFlexChildren.length > 0 && nodeAbsChildren.length === 0) {
            return 'hug';
        } else {
            return 'fixed';
        }
    }

    if (ratio < CHILD_PERCENT_TO_STRETCH) {
        return 'fill';
    }

    if (parent.calculatedLayoutAbstraction?.widthSpacingStrategy !== 'hug') {
        return 'stretch';
    } else {
        return 'fixed';
    }
}

function removeMargin(node: EnrichedAstNode) {
    ['marginLeft', 'marginRight', 'marginBottom', 'marginTop'].forEach((margin) => {
        if (!node.calculatedLayoutAbstraction?.userMarginSet?.[margin]) {
            node.position[margin] = 0;
        }
    });
}

const removePadding = (node: EnrichedAstNode) => {
    delete node.styles['alignItems'];
    delete node.styles['justifyContent'];
    delete node.styles['flexDirection'];
    if (node.type !== 'Text') {
        delete node.styles['display'];
    }
    delete node.styles['gap'];

    ['paddingLeft', 'paddingRight', 'paddingBottom', 'paddingTop'].forEach((padding) => {
        if (!node.calculatedLayoutAbstraction?.userPaddingSet?.[padding]) {
            node.innerPosition[padding] = 0;
        }
    });

    recalculateInnerPosition(node);
};
